import { formatDelta } from '@baseline/shared';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';

import { ApiError, api, apiResponse } from '../api/client.js';
import { useAssemblies, useDashboard, useManualCompare, useRevisions } from '../api/queries.js';
import { EmptyState } from '../components/EmptyState.js';
import { useToast } from '../components/Toast.js';

import type {
  Assembly,
  CompareRow,
  DiffStatus,
  ManualCompareRequest,
  ManualCompareSide,
  RowError,
  SourceSystem,
  UploadResponse,
} from '@baseline/shared';

// Prototype `vCompare` (plan Phase 11). The comparison itself runs in compare-service; this
// page only picks the two sides, filters and folds the rows it gets back.

type SideKey = 'A' | 'B';
type Sys = SourceSystem | 'UPLOAD';
interface Side {
  sys: Sys;
  assemblyId: string | null;
  rev: string | null;
  upload: UploadResponse | null;
  errs: RowError[];
}
type View = 'engineering' | 'purchasing';

const STATUS_ORDER: DiffStatus[] = ['Added', 'Removed', 'Changed', 'Moved', 'Unchanged'];

function hasBom(a: Assembly, sys: Sys): boolean {
  if (sys === 'PDM') return a.currentPdmRev !== null;
  if (sys === 'ERP') return a.currentErpRev !== null;
  return false;
}

function sideRequest(s: Side): ManualCompareSide | null {
  if (s.sys === 'UPLOAD') return s.upload ? { uploadId: s.upload.uploadId } : null;
  if (!s.assemblyId || !s.rev) return null;
  return { system: s.sys, assemblyId: s.assemblyId, rev: s.rev };
}

interface TreeRow extends CompareRow {
  ctx: boolean;
  kids: boolean;
  below: number;
  changed: number;
}

/** Prototype `treeView`: matches plus their parents for context, minus folded branches. */
export function treeView(rows: CompareRow[], tabs: Set<string>, folded: Set<string>): TreeRow[] {
  const byKey = new Map(rows.map((r) => [r.key, r]));
  const filtering = tabs.size > 0;
  let keep = rows;
  if (filtering) {
    const ks = new Set<string>();
    for (const r of rows) {
      if (!tabs.has(r.status)) continue;
      let c: CompareRow | undefined = r;
      while (c) {
        ks.add(c.key);
        c = byKey.get(c.pkey);
      }
    }
    keep = rows.filter((r) => ks.has(r.key));
  }
  const out: TreeRow[] = [];
  keep.forEach((r, i) => {
    const next = keep[i + 1];
    const kids = !!next && next.depth > r.depth;
    let hidden = false;
    let c = byKey.get(r.pkey);
    while (c) {
      if (folded.has(c.key)) {
        hidden = true;
        break;
      }
      c = byKey.get(c.pkey);
    }
    let below = 0;
    let changed = 0;
    if (kids) {
      const at = rows.indexOf(r);
      for (let j = at + 1; j < rows.length && (rows[j]?.depth ?? 0) > r.depth; j += 1) {
        below += 1;
        if (rows[j]?.status !== 'Unchanged') changed += 1;
      }
    }
    if (!hidden) out.push({ ...r, ctx: filtering && !tabs.has(r.status), kids, below, changed });
  });
  return out;
}

function SourceBar({
  sd,
  side,
  assemblies,
  onChange,
  onUpload,
}: {
  sd: SideKey;
  side: Side;
  assemblies: Assembly[];
  onChange: (s: Side) => void;
  onUpload: (file: File) => void;
}) {
  const revisions = useRevisions(
    side.sys === 'UPLOAD' ? null : side.assemblyId,
    side.sys === 'UPLOAD' ? null : side.sys,
  );
  const input = useRef<HTMLInputElement>(null);
  const revs = useMemo(
    () => [...new Set((revisions.data?.revisions ?? []).map((r) => r.rev))].sort(),
    [revisions.data],
  );
  const choices = assemblies.filter((a) => hasBom(a, side.sys));

  // Keep the pick valid, like the prototype's `fixSide`.
  useEffect(() => {
    if (side.sys === 'UPLOAD' || !choices.length) return;
    if (!choices.some((a) => a.id === side.assemblyId)) {
      onChange({ ...side, assemblyId: choices[0]?.id ?? null, rev: null });
    } else if (revs.length && (!side.rev || !revs.includes(side.rev))) {
      onChange({ ...side, rev: revs[revs.length - 1] ?? null });
    }
  });

  return (
    <>
      <div
        className="src"
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          const file = e.dataTransfer.files[0];
          if (side.sys === 'UPLOAD' && file) onUpload(file);
        }}
      >
        <span className={`src-l ${sd === 'A' ? 'src-a' : 'src-b'}`}>
          <i />
          File {sd}
        </span>
        <select
          id={`sel-${sd}-sys`}
          aria-label={`File ${sd} system`}
          value={side.sys}
          onChange={(e) =>
            onChange({ ...side, sys: e.target.value as Sys, rev: null, upload: null, errs: [] })
          }
        >
          <option value="PDM">PDM</option>
          <option value="ERP">ERP</option>
          <option value="UPLOAD">Upload CSV</option>
        </select>
        {side.sys !== 'UPLOAD' ? (
          <>
            <select
              id={`sel-${sd}-asm`}
              aria-label={`File ${sd} assembly`}
              value={side.assemblyId ?? ''}
              onChange={(e) => onChange({ ...side, assemblyId: e.target.value, rev: null })}
            >
              {choices.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
            <select
              id={`sel-${sd}-rev`}
              aria-label={`File ${sd} revision`}
              value={side.rev ?? ''}
              onChange={(e) => onChange({ ...side, rev: e.target.value })}
            >
              {revs.map((r) => (
                <option key={r} value={r}>
                  Rev {r}
                </option>
              ))}
            </select>
          </>
        ) : side.upload ? (
          <span className="fchip2">
            {side.upload.name} · {side.upload.lineCount} lines{' '}
            <button
              type="button"
              aria-label="Remove file"
              onClick={() => onChange({ ...side, upload: null, errs: [] })}
            >
              ✕
            </button>
          </span>
        ) : (
          <button className="btn" type="button" onClick={() => input.current?.click()}>
            Choose file…
          </button>
        )}
        <input
          ref={input}
          type="file"
          id={`in-${sd}`}
          accept=".csv"
          hidden
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) onUpload(file);
            e.target.value = '';
          }}
        />
      </div>
      {side.errs.length ? (
        <div className="errbox" role="alert">
          <b>File rejected. Nothing was loaded.</b>
          <ul>
            {side.errs.slice(0, 8).map((e, i) => (
              <li key={i}>
                {e.row ? `Row ${String(e.row)}: ` : ''}
                {e.msg}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </>
  );
}

export default function ComparePage() {
  const [params] = useSearchParams();
  const assemblies = useAssemblies();
  const dashboard = useDashboard();
  const toast = useToast();
  const list = useMemo(() => assemblies.data?.assemblies ?? [], [assemblies.data]);
  const [sides, setSides] = useState<Record<SideKey, Side> | null>(null);
  const [view, setView] = useState<View>('engineering');
  const [ignore, setIgnore] = useState(false);
  const [tabs, setTabs] = useState<Set<string>>(new Set());
  const [tabOpen, setTabOpen] = useState(false);
  const [folded, setFolded] = useState<Set<string>>(new Set());
  const menu = useRef<HTMLDivElement>(null);

  // Start from the query (the detail page's "Open full comparison") or the first assembly.
  useEffect(() => {
    if (sides || !list.length || (!dashboard.data && !dashboard.error)) return;
    // Else the assembly with the most open alerts, as the dashboard ranks them.
    const busiest = dashboard.data?.byAssembly.find((b) => b.open > 0)?.assemblyId;
    const both = (a: Assembly) => a.currentPdmRev !== null && a.currentErpRev !== null;
    const asm =
      list.find((a) => a.id === params.get('assemblyId')) ??
      list.find((a) => a.id === busiest && both(a)) ??
      list.find(both) ??
      list[0];
    if (!asm) return;
    setSides({
      A: {
        sys: 'PDM',
        assemblyId: asm.id,
        rev: params.get('aRev') ?? asm.currentPdmRev,
        upload: null,
        errs: [],
      },
      B: {
        sys: 'ERP',
        assemblyId: asm.id,
        rev: params.get('bRev') ?? asm.currentErpRev,
        upload: null,
        errs: [],
      },
    });
  }, [list, params, sides, dashboard.data, dashboard.error]);

  useEffect(() => {
    if (!tabOpen) return;
    const onClick = (e: MouseEvent) => {
      if (!menu.current?.contains(e.target as Node)) setTabOpen(false);
    };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, [tabOpen]);

  const a = sides ? sideRequest(sides.A) : null;
  const b = sides ? sideRequest(sides.B) : null;
  const request: ManualCompareRequest | null =
    a && b ? { a, b, view, ignoreLeadingZeros: ignore } : null;
  const result = useManualCompare(request);
  const rows = request ? result.data?.rows : undefined;
  const eng = view === 'engineering';

  const setSide = (sd: SideKey, s: Side) =>
    setSides((prev) => (prev ? { ...prev, [sd]: s } : prev));

  const upload = async (sd: SideKey, file: File) => {
    const form = new FormData();
    form.append('file', file);
    try {
      const res = await api<UploadResponse>('/compare/uploads', { method: 'POST', body: form });
      setSides((prev) => (prev ? { ...prev, [sd]: { ...prev[sd], upload: res, errs: [] } } : prev));
    } catch (err) {
      const details =
        err instanceof ApiError ? (err.details as { errors?: RowError[] } | undefined) : undefined;
      const errs = details?.errors ?? [
        { row: 0, msg: err instanceof ApiError ? err.message : 'The file could not be read.' },
      ];
      setSides((prev) => (prev ? { ...prev, [sd]: { ...prev[sd], upload: null, errs } } : prev));
    }
  };

  const exportCsv = async () => {
    if (!request) return;
    try {
      const res = await apiResponse('/compare/export', {
        method: 'POST',
        body: { ...request, ...(tabs.size ? { statuses: [...tabs] } : {}) },
      });
      const url = URL.createObjectURL(await res.blob());
      const link = document.createElement('a');
      link.href = url;
      link.download = 'bom_compare.csv';
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast('Downloading bom_compare.csv…');
    } catch {
      toast('Downloads are not available in this view.');
    }
  };

  const counts = (s: DiffStatus) => rows?.filter((r) => r.status === s).length ?? 0;
  const statusOpts = STATUS_ORDER.filter((s) => eng || s !== 'Moved');
  const activeTabs = new Set([...tabs].filter((t) => eng || t !== 'Moved'));
  const selected = statusOpts.filter((s) => activeTabs.has(s));
  const tabLabel = !activeTabs.size
    ? `Status: All (${String(rows?.length ?? 0)})`
    : selected.length === 1 && selected[0]
      ? `Status: ${selected[0]} (${String(counts(selected[0]))})`
      : `Status: ${String(selected.length)} selected`;

  const shown: TreeRow[] = rows
    ? eng
      ? treeView(rows, activeTabs, folded)
      : rows
          .filter((r) => !activeTabs.size || activeTabs.has(r.status))
          .map((r) => ({ ...r, ctx: false, kids: false, below: 0, changed: 0 }))
    : [];

  const toggleFold = (key: string) =>
    setFolded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const collapseAll = () => {
    const next = new Set<string>();
    (rows ?? []).forEach((r, i) => {
      const nx = rows?.[i + 1];
      if (r.depth >= 1 && nx && nx.depth > r.depth) next.add(r.key);
    });
    setFolded(next);
  };

  const toggleTab = (s: DiffStatus, on: boolean) =>
    setTabs((prev) => {
      const next = new Set(prev);
      if (on) next.add(s);
      else next.delete(s);
      return next;
    });

  return (
    <div className="pg wide">
      <div className="ph">
        <div>
          <h1>Compare</h1>
          <p className="ps">Compare two BOMs from PDM, ERP or an uploaded CSV.</p>
        </div>
        <div className="ph-r">
          <button className="btn" id="dl-csv" type="button" onClick={() => void exportCsv()}>
            Export CSV
          </button>
        </div>
      </div>
      <div className="srcs">
        {sides
          ? (['A', 'B'] as const).map((sd) => (
              <SourceBar
                key={sd}
                sd={sd}
                side={sides[sd]}
                assemblies={list}
                onChange={(s) => setSide(sd, s)}
                onUpload={(f) => void upload(sd, f)}
              />
            ))
          : null}
      </div>
      <div className="tools">
        <div className="seg" role="group" aria-label="View">
          <button type="button" aria-pressed={eng} onClick={() => setView('engineering')}>
            Engineering
          </button>
          <button type="button" aria-pressed={!eng} onClick={() => setView('purchasing')}>
            Purchasing
          </button>
        </div>
        <span className="hint">
          {eng
            ? 'Parent-child structure. Use the arrows to collapse a branch.'
            : 'Total quantity to buy of each part, across every place it is used.'}
        </span>
        {eng ? (
          <span className="tree-btns">
            <button className="btn" type="button" onClick={() => setFolded(new Set())}>
              Expand all
            </button>
            <button className="btn" type="button" onClick={collapseAll}>
              Collapse all
            </button>
          </span>
        ) : null}
        <label className="chk">
          <input
            type="checkbox"
            id="ignore0"
            checked={ignore}
            onChange={(e) => setIgnore(e.target.checked)}
          />{' '}
          Ignore leading zeros
        </label>
      </div>
      {!request || (!rows && result.error) ? (
        <EmptyState
          title="Pick two BOMs to compare."
          sub="Choose a system and revision for File A and File B, or upload a CSV."
        />
      ) : !rows ? null : (
        <>
          <div className="fbar">
            <div className="msel" id="c-tab-wrap" ref={menu}>
              <button
                type="button"
                className={`chip${activeTabs.size ? ' on' : ''}`}
                id="c-tab-btn"
                aria-haspopup="true"
                aria-expanded={tabOpen}
                onClick={() => setTabOpen((o) => !o)}
              >
                {tabLabel}
                <svg
                  width="10"
                  height="6"
                  viewBox="0 0 10 6"
                  fill="none"
                  stroke="#5B7B77"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{ marginLeft: 8, flex: 'none' }}
                  aria-hidden="true"
                >
                  <path d="M1 1l4 4 4-4" />
                </svg>
              </button>
              {tabOpen ? (
                <div className="msel-pop" role="menu">
                  <label className="msel-o">
                    <input
                      type="checkbox"
                      checked={!activeTabs.size}
                      onChange={() => setTabs(new Set())}
                    />
                    <span>All</span>
                    <em>{rows.length}</em>
                  </label>
                  {statusOpts.map((s) => (
                    <label key={s} className="msel-o">
                      <input
                        type="checkbox"
                        checked={activeTabs.has(s)}
                        onChange={(e) => toggleTab(s, e.target.checked)}
                      />
                      <span>{s}</span>
                      <em>{counts(s)}</em>
                    </label>
                  ))}
                </div>
              ) : null}
            </div>
          </div>
          <div className="cmp">
            <div className={`cg ch${eng ? ' eng' : ''}`}>
              <div>Status</div>
              {eng ? (
                <>
                  <div className="fn">Find No.</div>
                  <div className="sq">Seq No.</div>
                </>
              ) : null}
              <div>{eng ? 'Part No. (structure)' : 'Part No.'}</div>
              <div>Description</div>
              <div className="r">
                <i className="mkA" />
                {eng ? 'Qty A' : 'Total A'}
              </div>
              <div className="r">
                <i className="mkB" />
                {eng ? 'Qty B' : 'Total B'}
              </div>
              <div className="r">Delta</div>
            </div>
            {shown.length ? (
              shown.map((r) => {
                const isFolded = folded.has(r.key);
                return (
                  <div
                    key={r.key}
                    className={`cg cr${eng ? ' eng' : ''} r-${r.status.toLowerCase()}${r.ctx ? ' ctxrow' : ''}${eng && r.kids ? ' par' : ''}`}
                  >
                    <div className="st">{r.ctx ? '' : r.status}</div>
                    {eng ? (
                      <>
                        <div className="fn mono-s">{r.findNo ?? '—'}</div>
                        <div className="sq mono-s">{r.seqNo ?? '—'}</div>
                      </>
                    ) : null}
                    <div className="tc">
                      {eng
                        ? Array.from({ length: r.depth }, (_, i) => (
                            <span key={i} className="ind" />
                          ))
                        : null}
                      {eng ? (
                        r.kids ? (
                          <button
                            className="tw"
                            type="button"
                            aria-expanded={!isFolded}
                            aria-label={`${isFolded ? 'Expand' : 'Collapse'} ${r.description}`}
                            onClick={() => toggleFold(r.key)}
                          >
                            <span className={`tw-i${isFolded ? '' : ' open'}`}>
                              <svg
                                width="12"
                                height="12"
                                viewBox="0 0 12 12"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1.6"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                aria-hidden="true"
                              >
                                <path d="m4 2.5 3.5 3.5L4 9.5" />
                              </svg>
                            </span>
                          </button>
                        ) : (
                          <span className="tw-sp" />
                        )
                      ) : null}
                      <span className="pn">{r.pn}</span>
                    </div>
                    <div className="ds">
                      <span className="dtxt">{r.description}</span>
                      {eng && r.kids && isFolded ? (
                        <span className={`roll${r.changed ? ' has' : ''}`}>
                          {r.below} below{r.changed ? ` · ${String(r.changed)} changed` : ''}
                        </span>
                      ) : null}
                      {r.note ? (
                        <>
                          {' '}
                          <span className="hint">{r.note}</span>
                        </>
                      ) : null}
                    </div>
                    <div className="q">{r.qtyA ?? '—'}</div>
                    <div className="q">{r.qtyB ?? '—'}</div>
                    <div className="dl">{formatDelta(r.delta)}</div>
                  </div>
                );
              })
            ) : (
              <EmptyState title="No lines in this category." sub="Try another tab." />
            )}
          </div>
        </>
      )}
    </div>
  );
}
